import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { AdminShell } from "@/components/admin/admin-shell";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Activity, Gauge, HardDrive, Timer, Package, RefreshCw } from "lucide-react";
import {
  getSnapshot,
  subscribePerf,
  rateVital,
  startPerformanceMonitor,
  type PerfSnapshot,
  type VitalName,
} from "@/lib/performance-monitor";

export const Route = createFileRoute("/admin/performance")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Performance Monitor — MAKAI Admin" },
      { name: "description", content: "Live web vitals, API latency, bundle sizes and memory usage for the MAKAI platform." },
      { property: "og:title", content: "Performance Monitor — MAKAI Admin" },
      { property: "og:description", content: "Live web vitals, API latency, bundle sizes and memory usage for the MAKAI platform." },
    ],
  }),
  component: AdminPerformancePage,
});

const VITALS: { name: VitalName; label: string; unit: string }[] = [
  { name: "LCP", label: "Largest Contentful Paint", unit: "ms" },
  { name: "FCP", label: "First Contentful Paint", unit: "ms" },
  { name: "INP", label: "Interaction to Next Paint", unit: "ms" },
  { name: "CLS", label: "Cumulative Layout Shift", unit: "" },
  { name: "TTFB", label: "Time to First Byte", unit: "ms" },
];

const TONE: Record<string, string> = {
  good: "bg-emerald-500/10 text-emerald-600 ring-emerald-500/30",
  ok: "bg-amber-500/10 text-amber-600 ring-amber-500/30",
  poor: "bg-destructive/10 text-destructive ring-destructive/30",
  none: "bg-muted text-muted-foreground ring-border",
};

function AdminPerformancePage() {
  const [snap, setSnap] = useState<PerfSnapshot | null>(null);

  useEffect(() => {
    startPerformanceMonitor();
    setSnap(getSnapshot());
    const unsub = subscribePerf(() => setSnap(getSnapshot()));
    const t = setInterval(() => setSnap(getSnapshot()), 4000);
    return () => {
      unsub();
      clearInterval(t);
    };
  }, []);

  const apiStats = useMemo(() => {
    if (!snap) return { rows: [], p95: 0, avg: 0, count: 0 };
    const byUrl = new Map<string, number[]>();
    for (const s of snap.api) {
      const list = byUrl.get(s.url) ?? [];
      list.push(s.ms);
      byUrl.set(s.url, list);
    }
    const rows = [...byUrl.entries()]
      .map(([url, ms]) => ({
        url,
        calls: ms.length,
        avg: Math.round(ms.reduce((a, b) => a + b, 0) / ms.length),
        max: Math.max(...ms),
      }))
      .sort((a, b) => b.avg - a.avg)
      .slice(0, 12);
    const all = snap.api.map((s) => s.ms).sort((a, b) => a - b);
    return {
      rows,
      count: all.length,
      avg: all.length ? Math.round(all.reduce((a, b) => a + b, 0) / all.length) : 0,
      p95: all.length ? all[Math.min(all.length - 1, Math.floor(all.length * 0.95))] : 0,
    };
  }, [snap]);

  const slowRoutes = useMemo(() => {
    if (!snap) return [];
    const byPath = new Map<string, number[]>();
    for (const r of snap.routes) {
      const list = byPath.get(r.path) ?? [];
      list.push(r.ms);
      byPath.set(r.path, list);
    }
    return [...byPath.entries()]
      .map(([path, ms]) => ({
        path,
        visits: ms.length,
        avg: Math.round(ms.reduce((a, b) => a + b, 0) / ms.length),
        max: Math.max(...ms),
      }))
      .sort((a, b) => b.avg - a.avg)
      .slice(0, 12);
  }, [snap]);

  const totalKb = snap ? snap.resources.reduce((a, r) => a + r.kb, 0) : 0;

  return (
    <AdminShell>
      <div className="mx-auto w-full max-w-6xl space-y-6 p-4 md:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <Link to="/admin" className="mb-2 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-3.5 w-3.5" /> Admin
            </Link>
            <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
              <Activity className="h-5 w-5 text-primary" /> Performance monitor
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Live, in-session measurements from this device — web vitals, network latency, chunk sizes and memory.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setSnap(getSnapshot())}>
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Refresh
          </Button>
        </div>

        {/* Web vitals */}
        <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {VITALS.map((v) => {
            const value = snap?.vitals[v.name];
            const tone = value === undefined ? "none" : rateVital(v.name, value);
            return (
              <div key={v.name} className={`rounded-xl p-4 ring-1 ${TONE[tone]}`}>
                <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide opacity-80">
                  <Gauge className="h-3.5 w-3.5" /> {v.name}
                </div>
                <div className="mt-2 text-2xl font-semibold tabular-nums">
                  {value === undefined ? "—" : `${value}${v.unit}`}
                </div>
                <div className="mt-1 text-[11px] leading-tight opacity-75">{v.label}</div>
              </div>
            );
          })}
        </section>

        {/* Memory + bundle summary */}
        <section className="grid gap-3 sm:grid-cols-3">
          <Stat
            icon={<HardDrive className="h-4 w-4" />}
            label="JS heap in use"
            value={snap?.memoryMB != null ? `${snap.memoryMB} MB` : "Not reported"}
            sub={snap?.memoryLimitMB != null ? `Limit ${snap.memoryLimitMB} MB` : "Chrome/Android only"}
          />
          <Stat
            icon={<Package className="h-4 w-4" />}
            label="Code downloaded"
            value={`${totalKb} KB`}
            sub={`${snap?.resources.length ?? 0} JS/CSS files this session`}
          />
          <Stat
            icon={<Timer className="h-4 w-4" />}
            label="API latency"
            value={apiStats.count ? `${apiStats.avg} ms avg` : "—"}
            sub={apiStats.count ? `p95 ${apiStats.p95} ms · ${apiStats.count} requests` : "No requests yet"}
          />
        </section>

        {/* Slow routes */}
        <Panel title="Slowest navigations" empty={!slowRoutes.length} emptyText="Navigate around the app to collect route timings.">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr><th className="py-2">Route</th><th className="py-2 text-right">Visits</th><th className="py-2 text-right">Avg</th><th className="py-2 text-right">Worst</th></tr>
            </thead>
            <tbody>
              {slowRoutes.map((r) => (
                <tr key={r.path} className="border-t">
                  <td className="max-w-[280px] truncate py-2 font-medium">{r.path}</td>
                  <td className="py-2 text-right tabular-nums">{r.visits}</td>
                  <td className="py-2 text-right tabular-nums">{r.avg} ms</td>
                  <td className="py-2 text-right tabular-nums text-muted-foreground">{r.max} ms</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>

        {/* Slow endpoints */}
        <Panel title="Slowest endpoints (API, server functions, database reads)" empty={!apiStats.rows.length} emptyText="No network activity recorded yet.">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr><th className="py-2">Endpoint</th><th className="py-2 text-right">Calls</th><th className="py-2 text-right">Avg</th><th className="py-2 text-right">Worst</th></tr>
            </thead>
            <tbody>
              {apiStats.rows.map((r) => (
                <tr key={r.url} className="border-t">
                  <td className="max-w-[320px] truncate py-2 font-medium">{r.url}</td>
                  <td className="py-2 text-right tabular-nums">{r.calls}</td>
                  <td className={`py-2 text-right tabular-nums ${r.avg > 1200 ? "text-destructive" : ""}`}>{r.avg} ms</td>
                  <td className="py-2 text-right tabular-nums text-muted-foreground">{r.max} ms</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>

        {/* Largest bundles */}
        <Panel title="Largest downloaded chunks" empty={!snap?.resources.length} emptyText="No resource timings available.">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr><th className="py-2">File</th><th className="py-2 text-right">Transferred</th><th className="py-2 text-right">Load time</th></tr>
            </thead>
            <tbody>
              {snap?.resources.map((r) => (
                <tr key={r.name} className="border-t">
                  <td className="max-w-[360px] truncate py-2 font-medium">{r.name}</td>
                  <td className="py-2 text-right tabular-nums">{r.kb} KB</td>
                  <td className="py-2 text-right tabular-nums text-muted-foreground">{r.ms} ms</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>

        <p className="text-xs text-muted-foreground">
          Measurements are collected in this browser session only and are never uploaded.
        </p>
      </div>
    </AdminShell>
  );
}

function Stat({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string; sub: string }) {
  return (
    <div className="rounded-xl border bg-card p-4">
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">{icon} {label}</div>
      <div className="mt-2 text-xl font-semibold tabular-nums">{value}</div>
      <div className="mt-1 text-[11px] text-muted-foreground">{sub}</div>
    </div>
  );
}

function Panel({ title, children, empty, emptyText }: { title: string; children: React.ReactNode; empty?: boolean; emptyText?: string }) {
  return (
    <section className="rounded-xl border bg-card p-4">
      <h2 className="mb-2 text-sm font-semibold">{title}</h2>
      {empty ? <p className="py-4 text-sm text-muted-foreground">{emptyText}</p> : <div className="overflow-x-auto">{children}</div>}
    </section>
  );
}
